import { Card } from '@/components/Card';
import { Loading } from '@/components/Loading';
import { Text } from '@/components/Text';
import { EventFeedbackForm } from '@/partials/dashboard/Events/EventFeedback/EventFeedbackForm';
import { submitEventFeedback } from '@/services/feedbackService';
import { useEventStore } from '@/stores/useEventStore';
import { CreateEventFeedbackForm } from '@/types/feedback';
import { handleError } from '@/utils/handleError';
import { FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

export const EventFeedback = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation('eventFeedback');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { event, showEvent, loading: eventLoading } = useEventStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      showEvent(Number(id));
    }
  }, [id, showEvent]);

  const handleSubmitFeedback = async (
    values: CreateEventFeedbackForm,
    { setErrors, setSubmitting }: FormikHelpers<CreateEventFeedbackForm>,
  ) => {
    if (!event) return;

    setSubmitting(true);
    setLoading(true);

    try {
      const { message } = await submitEventFeedback(event.id, values);
      toast.success(message);

      // Navigate back to event details after successful submission
      navigate(`/${i18n.language}/dashboard/events/${event.id}`);
    } catch (error: unknown) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // Show loading while fetching event
  if (eventLoading || !event) {
    return <Loading />;
  }

  return (
    <div className="p-4">
      <Card>
        <div className="flex min-h-[400px] flex-col items-center justify-center">
          {/* Event Info */}
          <div className="mb-8 text-center">
            <Text className="text-lg font-semibold text-gray-800">
              {event.name}
            </Text>
            <Text className="text-sm text-gray-600">
              {new Date(event.date).toLocaleDateString()}
            </Text>
          </div>

          {/* Feedback Form */}
          <EventFeedbackForm
            onSubmit={handleSubmitFeedback}
            isLoading={loading}
          />

          {/* Back Link */}
          <div className="mt-8">
            <button
              onClick={() => navigate(-1)}
              className="text-sm font-medium text-sky-600 underline hover:text-sky-800"
            >
              ← {tCommon('back')}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};
